import type React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { EVENTS, useTelemetryEmitter } from "../event-emitter"
import type { TelemetryValue } from "../event-emitter"
import { extractText } from "../utils"

// =============================================================================
// Styles
// =============================================================================
export const buttonBaseConfig = {
  base: "shrink-0 group/button outline-2 outline-offset-2 outline-transparent inline-flex items-center justify-center transition [&_svg]:pointer-events-none [&_svg]:shrink-0 focus-visible:outline-gray-900",
  variants: {
    variant: {
      primary:
        "bg-gray-900 text-sm leading-6 font-semibold text-white hover:bg-gray-800",
      brand:
        "bg-blue-500 text-sm leading-6 font-semibold text-gray-900 [&_svg]:text-gray-800 hover:bg-blue-400",
      secondary:
        "bg-gray-900/6 text-sm leading-6 font-semibold text-gray-900 [&_svg]:text-gray-800 hover:bg-gray-900/12",
      outlined:
        "inset-ring inset-ring-gray-200 bg-white text-sm leading-6 font-semibold text-gray-900 [&_svg]:text-gray-800 hover:bg-gray-900/6",
      tertiary:
        "bg-transparent text-sm leading-6 font-semibold text-gray-900 [&_svg]:text-gray-800 hover:bg-gray-900/6",
      unstyled: ""
    },
    size: {
      small:
        "rounded-sm p-1 gap-1 [&:not(:has(svg,[data-icon]))]:px-3 has-data-[icon=inline-start]:pl-2 has-data-[icon=inline-start]:pr-3 has-data-[icon=inline-end]:pl-3 has-data-[icon=inline-end]:pr-2",
      medium:
        "rounded p-2 gap-2 [&:not(:has(svg,[data-icon]))]:px-4 has-data-[icon=inline-start]:pl-3 has-data-[icon=inline-start]:pr-4 has-data-[icon=inline-end]:pl-4 has-data-[icon=inline-end]:pr-3",
      large:
        "rounded-lg p-3 gap-3 [&:not(:has(svg,[data-icon]))]:px-5 has-data-[icon=inline-start]:pl-4 has-data-[icon=inline-start]:pr-5 has-data-[icon=inline-end]:pl-5 has-data-[icon=inline-end]:pr-4"
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "medium"
  }
} as const

export const buttonBaseStyles = cva(buttonBaseConfig.base, buttonBaseConfig)

// =============================================================================
// Shared prop types
// =============================================================================
type Variant = NonNullable<VariantProps<typeof buttonBaseStyles>["variant"]>
type Size = NonNullable<VariantProps<typeof buttonBaseStyles>["size"]>

/**
 * Variant/size relationship shared by `Button` and `LinkButton`. `size` is
 * meaningless for the `unstyled` variant (it applies no size classes), so it is
 * disallowed there and available for every other variant.
 */
export type ButtonVariantProps =
  | { variant?: Exclude<Variant, "unstyled">; size?: Size }
  | { variant: "unstyled"; size?: never }

/**
 * Resolve the size passed to the CVA styles. The `unstyled` variant opts out of
 * size classes entirely; every other variant falls back to the `medium` default.
 *
 * Returns `null` — not `undefined` — for `unstyled`: CVA treats `undefined` as
 * "prop absent" and falls back to `defaultVariants.size` ("medium"), whereas
 * `null` short-circuits and emits no size classes.
 */
export const resolveButtonSize = (variant: Variant, size?: Size) =>
  variant === "unstyled" ? null : (size ?? "medium")

// =============================================================================
// Telemetry
// =============================================================================
/**
 * Telemetry prop shared by `Button` and `LinkButton`. See `useButtonTelemetry`.
 */
export type ButtonTelemetryProps = {
  /**
   * Data to emit alongside the `button_click` event. Merges with any telemetry
   * provided by an ancestor `Telemetry` provider (local values win). Set to
   * `false` to opt this button out of emitting.
   */
  telemetry?: TelemetryValue
}

/**
 * Shared `button_click` telemetry behavior for `Button` and `LinkButton`.
 *
 * Returns a click handler that emits a `BUTTON_CLICK` event (with the button's
 * text content and the resolved telemetry payload) and then calls the
 * consumer's `onClick`. Emission is skipped when telemetry resolves to `false`.
 */
export const useButtonTelemetry = <E extends React.MouseEvent<HTMLElement>>({
  telemetry,
  children,
  onClick
}: {
  telemetry?: TelemetryValue
  children?: React.ReactNode
  onClick?: (event: E) => void
}) => {
  const emitTelemetry = useTelemetryEmitter(telemetry)

  return (event: E) => {
    emitTelemetry(EVENTS.BUTTON_CLICK, { text: extractText(children) })
    onClick?.(event)
  }
}
